import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { jobApi } from '@/api/jobApi';
import { useUIStore } from '@/store/uiStore';
import { QK } from './queryKeys';
import type { CreateJobPosting, ApplyJobRequest, JobFilter, Job } from '@/types';
import { getApiErrorMessage } from '@/utils';

export function useGuestJobs(filters: JobFilter) {
  return useQuery({
    queryKey: QK.GUEST_JOBS_LIST(filters),
    queryFn: () => jobApi.getGuestList(filters),
    // keepPreviousData: true,
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 2,
  });
}

export function useJobs(filters: JobFilter) {
  return useQuery({
    queryKey: QK.JOBS_LIST(filters),
    queryFn: () => jobApi.getList(filters),
    // keepPreviousData: true,
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 2,
  });
}

export function useJob(id: string) {
  return useQuery({
    queryKey: QK.JOB(id),
    queryFn: () => jobApi.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useJobApplications(id: string) {
  return useQuery({
    queryKey: QK.JOB_APPS(id),
    queryFn: () => jobApi.getApplications(id),
    enabled: !!id,
  });
}

export function useMyJobs() {
  return useQuery({
    queryKey: QK.JOBS_MINE,
    queryFn: jobApi.getMine,
  });
}

export function useMyApplications() {
  return useQuery({
    queryKey: QK.MY_APPLICATIONS,
    queryFn: jobApi.getMyApplications,
  });
}

export function useCreateJobPosting() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: (data: CreateJobPosting) => jobApi.createJobPosting(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job-postings'] });
      addToast('success', 'Đăng tin tuyển dụng thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Đăng tin thất bại')),
  });
}

export function useUpdateJobPosting() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateJobPosting }) => jobApi.updateJobPosting(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job-postings'] });
      addToast('success', 'Cập nhật tin tuyển dụng thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Cập nhật tin thất bại')),
  });
}

export function usePatchJobPosting() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Job> }) => jobApi.patchJobPosting(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job-postings'] });
      qc.invalidateQueries({ queryKey: ['jobs-mine'] });
      addToast('success', 'Cập nhật tin tuyển dụng thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Cập nhật tin tuyển dụng thất bại')),
  });
}

export function useDeleteJobPosting() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: (id: string) => jobApi.deleteJobPosting(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job-postings'] });
      addToast('success', 'Đã xóa tin tuyển dụng');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Xóa tin thất bại')),
  });
}

export function useApplyJob() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ApplyJobRequest }) => jobApi.apply(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: QK.JOB_APPS(id) });
      qc.invalidateQueries({ queryKey: QK.MY_APPLICATIONS });
      addToast('success', 'Nộp hồ sơ thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Nộp hồ sơ thất bại')),
  });
}

export function useDeleteJob() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: (id: string) => jobApi.deleteJobPosting(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job-postings'] });
      addToast('success', 'Đã xóa tin tuyển dụng');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Xóa tin thất bại')),
  });
}

export function useJobCategories() {
  return useQuery({
    queryKey: ['job-categories'],
    queryFn: () => jobApi.getCategories(),
    staleTime: 1000 * 60 * 30, // 30 mins
  });
}
